import {
  GenerationProgress,
  useTimedStep,
} from "@rosc/ui/v32/generation-progress";
import {
  Check,
  Clock,
  CornerDownRight,
  FileText,
  Layers,
  Loader2,
  Undo2,
  X,
} from "lucide-react";
import type { JSX } from "react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { V3Button, V3IconButton } from "@/components/ui/v3/button";
import { ProposalReview } from "@/components/ui/visits/NoteAssistantPanel";
import { cn } from "@/lib/utils";
import { AttachSourceButton, SourceChips } from "../sources/AssistantSources";
import type { AssistantUnavailableReason } from "../WorkspaceAssistant";
import { AskBar, type AskSuggestion } from "./AskBar";
import {
  PreChartAddHint,
  type PreChartAddOffer,
  PreChartAddProgress,
} from "./PreChartFileActions";
import type { AskAssistant } from "./useAskAssistant";

// While the assistant writes its proposal (a pre-chart rewritten under a new
// template, or any asked edit): one request with no stages of its own, so
// reading gives way to writing after 3 s and stays current until it arrives.
const AssistantProposing = (): JSX.Element => {
  const { t } = useTranslation();
  const step = useTimedStep([3000]);
  return (
    <GenerationProgress
      current={step}
      plain
      steps={[
        { key: "reading", label: t("generationProgress.steps.readingNote") },
        {
          key: "writing",
          label: t("generationProgress.steps.writingChanges"),
        },
      ]}
      testId="assistant-proposing"
      title={t("noteAssistant.proposing")}
    />
  );
};

const REASON_KEY = {
  "no-text": "visit.workspace.assistantNeedsNote",
  editing: "visit.workspace.assistantWhileEditing",
  "old-version": "visit.workspace.assistantOldVersion",
  generating: "visit.workspace.assistantGenerating",
} as const satisfies Record<AssistantUnavailableReason, string>;

// Saved shortcuts ("Insert normal cardiac exam") and, with sources in use,
// "Add sources to note", as chips above the Ask bar.
export const useAskSuggestions = (
  ask: AskAssistant,
  sourceCount: number,
): AskSuggestion[] => {
  const { t } = useTranslation();
  return [
    ...(sourceCount > 0
      ? [
          {
            id: "sources",
            icon: "sources" as const,
            label: t("noteAssistant.incorporateButton"),
            onSelect: () =>
              ask.setDraft(t("noteAssistant.incorporateInstruction")),
          },
        ]
      : []),
    ...ask.shortcuts.map((macro) => ({
      id: `shortcut-${macro.public_id}`,
      icon: "shortcut" as const,
      label: macro.name,
      onSelect: () => ask.insertShortcut(macro.name),
    })),
  ];
};

interface Props {
  ask: AskAssistant;
  documentName: string;
  unavailable?: AssistantUnavailableReason | null;
  sourceCount: number;
  withSources: boolean;
  // A pre-chart draft: the intro and placeholder ask for dictation and files.
  preChart?: boolean;
  // A pre-chart's files waiting to be added: Spread into sections or
  // Summarize and insert above this Ask bar too.
  addOffer?: PreChartAddOffer | null;
}

// The v3.2 assistant (iOS AssistantSheet), laid out as a chat: what was
// asked on the right, the proposal with its clinical fact check and diff
// under it, Discard / Apply just above the Ask bar, which is pinned at the
// bottom. Same propose, fact check and versioned apply as before. A request
// sent while a proposal is under review refines it: the requests show as
// steps, and the one proposal shows every change against the saved note.
export const AssistantConversation = ({
  ask,
  documentName,
  unavailable,
  sourceCount,
  withSources,
  preChart = false,
  addOffer = null,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const { result, proposing, applying, proposeFailed, sent, steps } = ask;
  const [acknowledgedFor, setAcknowledgedFor] = useState<typeof result>(null);
  const acknowledged = !!result && acknowledgedFor === result;
  const unchanged = result ? result.proposed === result.current : false;
  const allSuggestions = useAskSuggestions(ask, sourceCount);
  // The hint offers the ways to add the files: not "Add sources to note"
  // as well.
  const suggestions = addOffer
    ? allSuggestions.filter((suggestion) => suggestion.id !== "sources")
    : allSuggestions;
  const scroller = useRef<HTMLDivElement>(null);
  const showRequest = !!sent && (proposing || !!result || proposeFailed);

  // A new proposal scrolls into view from its top.
  useEffect(() => {
    if (!result || proposing) return;
    requestAnimationFrame(() => {
      scroller.current
        ?.querySelector('[data-testid="note-proposal"]')
        ?.scrollIntoView?.({ block: "start" });
    });
  }, [result, proposing]);

  return (
    <div className="flex h-full min-h-0 flex-col font-v3 text-v3-text">
      <div
        ref={scroller}
        className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 pb-6 pt-1"
        data-testid="assistant-conversation"
      >
        <div>
          <p className="text-[14px] font-semibold">
            {t("v3.ask.editing", { name: documentName })}
          </p>
          {!result && (
            <p className="mt-1 text-[13.5px] leading-relaxed text-v3-text-2">
              {preChart ? t("preChart.ask.intro") : t("v3.ask.intro")}
            </p>
          )}
        </div>
        {unavailable && (
          <p
            className="rounded-[14px] bg-v3-surface-2 px-4 py-3 text-[14px] text-v3-text-2"
            data-testid="assistant-unavailable"
            role="status"
          >
            {t(REASON_KEY[unavailable])}
          </p>
        )}
        {ask.origin?.origin === "transcript-edit" && showRequest && (
          <div
            className="rounded-[14px] px-4 py-3 shadow-v3-hairline-strong"
            data-testid="assistant-transcript-origin"
          >
            <p className="flex items-center gap-2 text-[14px] font-semibold">
              <FileText aria-hidden className="size-4 shrink-0 text-v3-brand" />
              {t("noteAssistant.fromTranscript", {
                count: ask.origin.changes,
              })}
            </p>
            <p className="mt-1 text-[13px] leading-relaxed text-v3-text-2">
              {t("noteAssistant.fromTranscriptHint")}
            </p>
            {ask.origin.omitted > 0 && (
              <p className="mt-1 text-[13px] text-v3-rec-text">
                {t("noteAssistant.omittedCorrections", {
                  count: ask.origin.omitted,
                })}
              </p>
            )}
          </div>
        )}
        {ask.applied.length > 0 && (
          <ol
            aria-label={t("noteAssistant.appliedLabel")}
            className="space-y-3"
          >
            {ask.applied.map((edit) => (
              <li
                key={edit.id}
                className="flex flex-col items-end gap-1.5 pl-12"
                data-testid="assistant-applied"
              >
                <p className="whitespace-pre-wrap rounded-[18px] rounded-br-[6px] bg-v3-surface-2 px-4 py-2.5 text-[14.5px] leading-relaxed text-v3-text-2">
                  {ask.shown(edit.instruction)}
                </p>
                <div className="flex items-center gap-1 text-[13px] text-v3-text-3">
                  {edit.undone ? (
                    <span data-testid="assistant-applied-undone">
                      {t("noteAssistant.undoneLabel")}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1">
                      <Check aria-hidden className="size-3.5 text-v3-ok" />
                      {t("noteAssistant.appliedLabel")}
                    </span>
                  )}
                  {ask.undoable?.id === edit.id && (
                    <V3Button
                      className="-mr-2"
                      data-testid="assistant-undo"
                      disabled={ask.undoing || proposing || applying}
                      variant="ghost"
                      onClick={() => void ask.undo(edit.id)}
                    >
                      {ask.undoing ? (
                        <Loader2 aria-hidden className="animate-spin" />
                      ) : (
                        <Undo2 aria-hidden />
                      )}
                      {t("noteAssistant.undo")}
                    </V3Button>
                  )}
                </div>
              </li>
            ))}
          </ol>
        )}
        {showRequest && (
          <ol className="space-y-3" data-testid="assistant-requests">
            {[...steps, sent].map((instruction, index) => (
              <li
                // biome-ignore lint/suspicious/noArrayIndexKey: steps only grow or shrink at the end.
                key={index}
                className="flex flex-col items-end gap-1.5 pl-12"
              >
                {index > 0 && (
                  <p
                    className="inline-flex items-center gap-1 text-[12.5px] font-medium text-v3-text-3"
                    data-testid="assistant-refining"
                  >
                    <CornerDownRight aria-hidden className="size-3.5" />
                    {t("noteAssistant.refiningLabel")}
                  </p>
                )}
                <p
                  className="whitespace-pre-wrap rounded-[18px] rounded-br-[6px] bg-v3-surface-2 px-4 py-2.5 text-[14.5px] leading-relaxed"
                  data-testid={
                    index === steps.length
                      ? "assistant-request"
                      : "assistant-step"
                  }
                >
                  {ask.shown(instruction)}
                </p>
              </li>
            ))}
          </ol>
        )}
        {ask.auto && (proposing || applying) ? (
          <PreChartAddProgress
            kind={ask.auto.kind}
            label={ask.auto.label}
            plain
            saving={applying}
          />
        ) : (
          proposing && <AssistantProposing />
        )}
        {proposeFailed && !proposing && (
          <div
            className="flex flex-wrap items-center justify-between gap-2 rounded-[14px] bg-v3-rec-tint px-4 py-2 text-[14px] text-v3-rec-text"
            role="alert"
          >
            {t("noteAssistant.proposeError")}
            <div className="flex flex-wrap gap-2">
              {steps.length > 0 && (
                <V3Button
                  data-testid="proposal-back"
                  variant="ghost"
                  onClick={ask.back}
                >
                  {t("noteAssistant.backToPrevious")}
                </V3Button>
              )}
              <V3Button variant="secondary" onClick={ask.retry}>
                {t("noteAssistant.retry")}
              </V3Button>
            </div>
          </div>
        )}
        {ask.queue.length > 0 && (
          <ol
            aria-label={t("noteAssistant.queuedLabel")}
            className="space-y-2"
            data-testid="assistant-queue"
          >
            {ask.queue.map((item) => (
              <li
                key={item.id}
                className="flex items-start justify-end gap-1 pl-12"
                data-testid="assistant-queued"
              >
                <div className="min-w-0 rounded-[18px] rounded-br-[6px] px-4 py-2.5 shadow-v3-hairline-strong">
                  <p className="flex items-center gap-1.5 text-[12.5px] font-semibold text-v3-text-3">
                    <Clock aria-hidden className="size-3.5" />
                    {t("noteAssistant.queuedLabel")}
                  </p>
                  <p className="whitespace-pre-wrap text-[14.5px] leading-relaxed">
                    {ask.shown(item.instruction)}
                  </p>
                </div>
                <V3IconButton
                  className="shrink-0"
                  data-testid="assistant-queued-remove"
                  label={t("noteAssistant.removeQueued")}
                  onClick={() => ask.removeQueued(item.id)}
                >
                  <X />
                </V3IconButton>
              </li>
            ))}
            <li className="pl-12 text-right text-[12.5px] text-v3-text-3">
              {ask.queueRefines
                ? t("noteAssistant.queueRefineHint")
                : t("noteAssistant.queueHint")}
            </li>
          </ol>
        )}
        {result && !proposing && (
          <div
            className="min-w-0 space-y-3 text-[14px] [&_.uppercase]:text-[13px] [&_.uppercase]:normal-case [&_.uppercase]:tracking-normal [&_[data-testid=fact-check]]:space-y-2.5"
            data-testid="note-proposal"
          >
            {steps.length > 0 && (
              <p
                className="flex items-center gap-1.5 text-[13px] text-v3-text-2"
                data-testid="proposal-combined"
              >
                <Layers aria-hidden className="size-4 shrink-0 text-v3-brand" />
                {t("noteAssistant.combinedHint", { count: steps.length + 1 })}
              </p>
            )}
            <ProposalReview
              preChart={preChart}
              acknowledged={acknowledged}
              fromTranscript={ask.origin?.origin === "transcript-edit"}
              result={result}
              onAcknowledgedChange={(value) =>
                setAcknowledgedFor(value ? result : null)
              }
            />
          </div>
        )}
      </div>
      {/* Full panel width with equal 16px insets, 20px above the bottom. */}
      <div className="shrink-0 space-y-2.5 bg-gradient-to-b from-transparent to-v3-panel to-30% px-4 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-2">
        {result && !proposing && (
          <div className="flex gap-2" data-testid="proposal-actions">
            <V3Button
              className="h-12 flex-1 text-[15px] lg:h-11"
              variant="secondary"
              data-testid="proposal-discard"
              onClick={ask.discard}
            >
              {steps.length > 0
                ? t("noteAssistant.discardAll")
                : t("noteAssistant.discardButton")}
            </V3Button>
            <V3Button
              className="h-12 flex-1 text-[15px] lg:h-11"
              data-testid="proposal-apply"
              disabled={
                applying || unchanged || (result.blocked && !acknowledged)
              }
              variant="primary"
              onClick={() => void ask.apply(acknowledged)}
            >
              {applying ? (
                <Loader2 aria-hidden className="animate-spin" />
              ) : (
                <Check aria-hidden />
              )}
              {t("noteAssistant.applyButton")}
            </V3Button>
          </div>
        )}
        {addOffer && !result && !proposing && !ask.draft.trim() && (
          <PreChartAddHint offer={addOffer} />
        )}
        <AskBar
          attach={
            withSources ? <AttachSourceButton camera={false} /> : undefined
          }
          attachments={withSources ? <SourceChips thumbnails /> : undefined}
          autoFocus={!result && !proposing}
          className={cn("max-w-none", unavailable && "opacity-60")}
          // Several edits in a row: what is sent while one is written,
          // reviewed or applied waits its turn.
          disabled={!!unavailable || ask.undoing}
          queueWhileBusy
          focusShortcut
          placeholder={
            result
              ? ask.canRefine
                ? t("v3.ask.placeholderRefine")
                : t("v3.ask.placeholderAgain")
              : preChart
                ? t("preChart.ask.placeholder")
                : t("v3.ask.placeholder")
          }
          proposing={proposing}
          suggestions={result ? [] : suggestions}
          testId="assistant-ask"
          value={ask.draft}
          onChange={ask.setDraft}
          onSend={() => ask.send()}
          onStop={ask.stop}
        />
      </div>
    </div>
  );
};
